/**
 * router/index.ts — Admin Vue Router configuration
 *
 * Auth guard redirects unauthenticated users to /login.
 * All admin views require authentication.
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

const routes: RouteRecordRaw[] = [
    /* -------------------------------------------------- */
    /* Auth routes (centered layout)                       */
    /* -------------------------------------------------- */
    {
        path: '/login',
        component: () => import('@/layouts/AuthLayout.vue'),
        children: [
            {
                path: '',
                name: 'admin-login',
                component: () => import('@/views/auth/AdminLogin.vue'),
            },
        ],
    },

    /* -------------------------------------------------- */
    /* Admin routes (sidebar layout, requires auth)        */
    /* -------------------------------------------------- */
    {
        path: '/',
        component: () => import('@/layouts/AdminLayout.vue'),
        meta: { requiresAuth: true },
        children: [
            {
                path: '',
                name: 'dashboard',
                component: () => import('@/views/dashboard/DashboardHome.vue'),
            },
            {
                path: 'users',
                name: 'users-list',
                component: () => import('@/views/users/UsersList.vue'),
            },
            {
                path: 'users/:id',
                name: 'user-detail',
                component: () => import('@/views/users/UserDetail.vue'),
            },
            {
                path: 'trades',
                name: 'trades-list',
                component: () => import('@/views/trades/TradesList.vue'),
            },
            {
                path: 'trades/:id',
                name: 'trade-detail',
                component: () => import('@/views/trades/TradeDetail.vue'),
            },
            {
                path: 'offers',
                name: 'offers-list',
                component: () => import('@/views/offers/OffersList.vue'),
            },
            {
                path: 'offers/:id',
                name: 'offer-detail',
                component: () => import('@/views/offers/OfferDetail.vue'),
            },
            {
                path: 'disputes',
                name: 'disputes-list',
                component: () => import('@/views/disputes/DisputesList.vue'),
            },
            {
                path: 'disputes/:id',
                name: 'dispute-detail',
                component: () => import('@/views/disputes/DisputeDetail.vue'),
            },
            {
                path: 'escrow',
                name: 'escrow-overview',
                component: () => import('@/views/escrow/EscrowOverview.vue'),
            },
            {
                path: 'waitlist',
                name: 'waitlist',
                component: () => import('@/views/waitlist/WaitlistView.vue'),
            },
            {
                path: 'settings',
                name: 'settings',
                component: () => import('@/views/settings/SettingsView.vue'),
            },
        ],
    },

    /* -------------------------------------------------- */
    /* Catch-all redirect                                  */
    /* -------------------------------------------------- */
    {
        path: '/:pathMatch(.*)*',
        redirect: '/',
    },
];

export const router = createRouter({
    history: createWebHistory(),
    routes,
});

/* -------------------------------------------------- */
/* Navigation guards                                   */
/* -------------------------------------------------- */
router.beforeEach((to, _from, next) => {
    /**
     * TODO: Replace with real auth state check from the auth store.
     * For now, we use a simple localStorage flag for scaffolding.
     */
    const isAuthenticated = localStorage.getItem('localusdt_admin_auth') === 'true';

    /** Debug: log navigation guard checks */
    console.log(`[ADMIN:ROUTER] Navigating to ${to.path} | auth=${isAuthenticated}`);

    if (to.meta.requiresAuth && !isAuthenticated) {
        console.log('[ADMIN:ROUTER] Redirecting to /login — not authenticated');
        return next({ name: 'admin-login' });
    }

    next();
});
